import moment from 'moment';
import { Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';

import { Injectable, OnDestroy, inject } from '@angular/core';

import { ApiService } from '@ng-gems/api';

import { UserSelfPatchParams } from '@app/core/http-models/account/user-self-patch-params.model';
import { UserSelfPreferencesPatchParams } from '@app/core/http-models/account/user-self-preferences-patch-params.model';
import { SiteGroup } from '@models/account/site-group.model';
import { User } from '@models/user.model';

import { AppInfoService } from './app-info.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class ProfileService implements OnDestroy {
  private api = inject(ApiService);
  private authService = inject(AuthService);
  private appInfoService = inject(AppInfoService);
  onUser: Observable<User> = this.authService.getUser();
  private user: User;

  private ngUnsubscribe = new Subject();

  constructor() {
    this.onUser.pipe(filter(Boolean), takeUntil(this.ngUnsubscribe)).subscribe({
      next: (user: User) => {
        this.user = user;
      },
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  getUser() {
    return this.user;
  }

  getSummaryReportSubscription(dagId: string, contactId: number) {
    return this.api.portal.getSummaryReportSubscription(dagId, contactId);
  }

  activateMailSubscription(dagId: string, contactId: number): Observable<unknown> {
    return this.api.portal.putEmailSubscription(dagId, contactId);
  }

  deactivateMailSubscription(dagId: string, contactId: number): Observable<unknown> {
    return this.api.portal.deleteEmailSubscription(dagId, contactId);
  }

  updateProfile(user: User): Observable<User> {
    return this.api.portalCustomer.editUserProfile(new UserSelfPatchParams(user)).pipe(switchMap(() => this.authService.refreshUser()));
  }

  updatePreferences(): Observable<User> {
    return this.api.portalCustomer
      .editUserProfile(new UserSelfPreferencesPatchParams(this.user))
      .pipe(switchMap(() => this.authService.refreshUser()));
  }

  updateBoardDateAndPreferences(): Observable<User> {
    this.user.onBoardDate = moment();
    return this.updatePreferences();
  }

  setSiteGroups(siteGroups: SiteGroup[]): Observable<User> {
    this.user.siteGroups = siteGroups;
    return this.updatePreferences();
  }

  setPreferredCountriesAndAccounts(preferredCountries: string[], preferredAccounts: number[]) {
    this.user.preferredCountries = preferredCountries;
    this.user.preferredAccounts = preferredAccounts;
  }

  updateTermsOfUseAcceptance(isTermsOfUseAccepted: boolean): Observable<User> {
    this.user.isTermsOfUseAccepted = isTermsOfUseAccepted;
    this.user.termsOfUseAcceptanceDate = isTermsOfUseAccepted ? moment() : null;
    return this.updatePreferences();
  }

  isTermsOfUseCompliant(): boolean {
    return (
      this.user &&
      this.user.isTermsOfUseAccepted &&
      moment(this.user.termsOfUseAcceptanceDate).isValid() &&
      moment(this.user.termsOfUseAcceptanceDate).isSameOrAfter(this.appInfoService.loadedEnvironment.termsOfUseLastUpdated)
    );
  }

  getAvatar(user: User) {
    if (user && user.hasAvatar) {
      return this.api.userContact.getAvatar(user.id).pipe(map((res) => URL.createObjectURL(res)));
    }
    return of(null);
  }
}
